import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  Drawer,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { useLocation, Link } from 'react-router-dom';
import PersonIcon from '../../svg/Profilenavicons/ProfileIcon';
import CreditCardIcon from '../../svg/Profilenavicons/PurchasesIcon';
import AttachMoneyIcon from '../../svg/Profilenavicons/PricecardIcon';
import DashboardIcon from '../../svg/Profilenavicons/DashboardIcon';
import BookIcon from '../../svg/Profilenavicons/MycoursesIcon';
import DescriptionIcon from '../../svg/Profilenavicons/DocumentIcon';
import LanguageIcon from '../../svg/Profilenavicons/WeblinksIcon';
import FavoriteIcon from '../../svg/Profilenavicons/SocialmediaIcon';
import MicIcon from '../../svg/Profilenavicons/VoiceSampleIcon';
import EmojiPeopleIcon from '../../svg/Profilenavicons/IntroductionIcon';
import { useAuth } from '../../hooks/useAuth';
import { USER_TYPE } from '../../config/const';
import { CreatePersonaButton } from './createPersonaButton';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  GridView,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Add,
  DashboardOutlined,
} from '@mui/icons-material';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';

const drawerWidth = 237;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  height: '100%',
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    height: '100%',
    boxSizing: 'border-box',
    borderRight: `1px solid ${theme.palette.divider}`,
    borderLeft: 'none',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : '#F9F9F9',
    position: 'relative',
  },
}));

const SubscriptionBox = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : '#F0F0F0',
  padding: '4px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}));

const SubscriptionHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const ProgressBarContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 8,
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#FAFAFA',
  '& .MuiLinearProgress-bar': {
    borderRadius: 8,
    backgroundColor: theme.palette.primary.main,
  },
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  textAlign: 'left',
  color: theme.palette.text.primary,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.14px',
  padding: '8px 16px',
}));

export const StyledListItemText = styled(Typography)<{ isselected: string }>(
  ({ theme, isselected }) => ({
    fontSize: 14,
    fontWeight: isselected === 'true' ? 600 : 400,
    lineHeight: '20.02px',
    letterSpacing: '0.17px',
    fontFamily:
      isselected === 'true'
        ? 'Merriweather Sans, sans-serif'
        : 'Inter, sans-serif',
    color:
      isselected === 'true'
        ? theme.palette.text.primary
        : theme.palette.text.secondary,
  })
);

export const ScrollableBox = styled(Box)({
  overflowY: 'auto',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const NavItem = styled(ListItemButton)<{
  isselected: string;
  component?: React.ElementType;
  to?: string;
}>(({ theme, isselected }) => ({
  padding: '4px 8px 4px 16px',
  height: 36,
  gap: 12,
  backgroundColor:
    isselected === 'true'
      ? theme.palette.mode === 'dark'
        ? 'rgba(51, 61, 255, 0.24)'
        : 'rgba(51, 61, 255, 0.12)'
      : 'transparent',
  '&:hover': {
    backgroundColor:
      isselected === 'true'
        ? theme.palette.mode === 'dark'
          ? 'rgba(51, 61, 255, 0.32)'
          : 'rgba(51, 61, 255, 0.16)'
        : 'rgba(0, 0, 0, 0.04)',
  },
}));

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  width: 24,
  height: 24,
  padding: 0,
  color: theme.palette.action.active,
}));

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  hasChevron?: boolean;
  hasPlus?: boolean;
  isExpanded?: boolean;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const ProfileNav: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    Training: true,
  });
  const location = useLocation();
  const { authUser } = useAuth();
  const theme = useTheme();

  // Mock subscription data - replace with actual data from your backend
  const subscriptionData = {
    used: 25,
    total: 1000,
  };

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const menuSections: MenuSection[] = useMemo(() => {
    if (authUser?.userType === USER_TYPE.CREATOR) {
      return [
        {
          title: 'My AI Persona',
          items: [
            {
              label: 'Training',
              icon: <DescriptionIcon color={theme.palette.action.active} />,
              path: `/${authUser?.userName}/update/documents`,
              hasChevron: true,
              hasPlus: true,
            },
          ],
        },
        {
          title: 'Monetization',
          items: [
            {
              label: 'Chat History',
              icon: (
                <DashboardOutlined
                  sx={{ fontSize: 18, color: theme.palette.action.active }}
                />
              ),
              path: `/${authUser?.userName}/chat/history`,
            },
            {
              label: 'Analytics',
              icon: (
                <BarChartOutlinedIcon
                  sx={{ fontSize: 18, color: theme.palette.action.active }}
                />
              ),
              path: `/${authUser?.userName}/analytics`,
            },
            {
              label: 'Products',
              icon: (
                <GridView
                  sx={{ fontSize: 18, color: theme.palette.action.active }}
                />
              ),
              path: `/${authUser?.userName}/products`,
              hasPlus: true,
            },
            {
              label: 'Price Card',
              icon: <AttachMoneyIcon color={theme.palette.action.active} />,
              path: `/${authUser?.userName}/revenue`,
            },
            {
              label: 'My Courses',
              icon: <BookIcon color={theme.palette.action.active} />,
              path: `/${authUser?.userName}/courses`,
              hasPlus: true,
            },
          ],
        },
        {
          title: 'Account',
          items: [
            {
              label: 'Profile',
              icon: <PersonIcon color={theme.palette.action.active} />,
              path: `/${authUser?.userName}`,
            },
            {
              label: 'Purchases',
              icon: <CreditCardIcon color={theme.palette.action.active} />,
              path: `/${authUser?.userName}/purchases`,
            },
            {
              label: 'Settings',
              icon: (
                <SettingsOutlinedIcon
                  sx={{ fontSize: 18, color: theme.palette.action.active }}
                />
              ),
              path: `/${authUser?.userName}/settings`,
            },
          ],
        },
      ];
    }
    return [
      {
        title: 'Account',
        items: [
          {
            label: 'Profile',
            icon: <PersonIcon color={theme.palette.action.active} />,
            path: `/${authUser?.userName}`,
          },
          {
            label: 'Purchases',
            icon: <CreditCardIcon color={theme.palette.action.active} />,
            path: `/${authUser?.userName}/purchases`,
          },
          {
            label: 'Settings',
            icon: (
              <SettingsOutlinedIcon
                sx={{ fontSize: 18, color: theme.palette.action.active }}
              />
            ),
            path: `/${authUser?.userName}/settings`,
          },
        ],
      },
      {
        title: 'AI Persona',
        items: [],
      },
    ];
  }, [authUser, theme]);

  // Training submenu items
  const trainingSubItems: MenuItem[] = useMemo(
    () => [
      {
        label: 'Documents',
        icon: <DescriptionIcon color={theme.palette.action.active} />,
        path: `/${authUser?.userName}/update/documents`,
      },
      {
        label: 'Journal',
        icon: <BookIcon color={theme.palette.action.active} />,
        path: `/${authUser?.userName}/update/journaling`,
      },
      {
        label: 'Weblinks',
        icon: <LanguageIcon color={theme.palette.action.active} />,
        path: `/${authUser?.userName}/update/weblinks`,
      },
      {
        label: 'Social Media',
        icon: <FavoriteIcon color={theme.palette.action.active} />,
        path: `/${authUser?.userName}/update/social`,
      },
      {
        label: 'Introduction',
        icon: <EmojiPeopleIcon color={theme.palette.action.active} />,
        path: `/${authUser?.userName}/update/introduction`,
      },
    ],
    [authUser, theme]
  );

  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth <= 900);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  const getSelectedPath = () => {
    const allPaths = menuSections.flatMap((section) =>
      section.items.map((item) => item.path)
    );
    const trainingPaths = trainingSubItems.map((item) => item.path);
    const allAvailablePaths = [...allPaths, ...trainingPaths];

    if (allAvailablePaths.includes(location.pathname)) {
      return location.pathname;
    }

    if (
      location.pathname.startsWith(
        `/${authUser?.userName}/update/journaling/newEntry`
      )
    ) {
      return `/${authUser?.userName}/update/journaling`;
    }

    return `/${authUser?.userName}`;
  };

  const selectedPath = getSelectedPath();

  const renderNavItem = (item: MenuItem, isSubItem = false) => {
    const isSelected = selectedPath === item.path;

    return (
      <NavItem
        key={item.label}
        component={Link}
        to={item.path}
        isselected={isSelected.toString()}
        sx={{
          pl: isSubItem ? 4 : 2,
        }}
        onClick={(e: React.MouseEvent) => {
          if (item.hasChevron) {
            e.preventDefault();
            toggleExpand(item.label);
          }
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 18,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <StyledListItemText isselected={isSelected.toString()} sx={{ flex: 1 }}>
          {item.label}
        </StyledListItemText>
        {item.hasChevron && (
          <ActionIconButton
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleExpand(item.label);
            }}
          >
            {expandedItems[item.label] ? (
              <KeyboardArrowUp fontSize='small' />
            ) : (
              <KeyboardArrowDown fontSize='small' />
            )}
          </ActionIconButton>
        )}
        {item.hasPlus && (
          <ActionIconButton
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Handle add action
            }}
          >
            <Add fontSize='small' />
          </ActionIconButton>
        )}
      </NavItem>
    );
  };

  return (
    <StyledDrawer
      variant={isMobile ? 'temporary' : 'permanent'}
      anchor='right'
      open
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Subscription Tier Section */}
        <SubscriptionBox>
          <SubscriptionHeader>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.14px',
                color: 'text.primary',
              }}
            >
              Subscription Tier
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                lineHeight: '22px',
                letterSpacing: '0.46px',
                color: 'action.active',
                cursor: 'pointer',
                px: '5px',
                py: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: 1,
                },
              }}
            >
              Edit
            </Typography>
          </SubscriptionHeader>
          <ProgressBarContainer>
            <StyledLinearProgress
              variant='determinate'
              value={(subscriptionData.used / subscriptionData.total) * 100}
            />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '15.6px',
                letterSpacing: '0.17px',
                color: 'text.secondary',
              }}
            >
              {subscriptionData.used}/{subscriptionData.total} Responses
            </Typography>
          </ProgressBarContainer>
        </SubscriptionBox>

        {/* Navigation Sections */}
        <ScrollableBox sx={{ pt: 1.5 }}>
          {menuSections.map((section, sectionIndex) => (
            <Box
              key={sectionIndex}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <SectionTitle>{section.title}</SectionTitle>
              <List disablePadding>
                {section.items.map((item) => (
                  <React.Fragment key={item.label}>
                    {renderNavItem(item)}
                    {/* Render Training submenu if expanded */}
                    {item.label === 'Training' &&
                      item.hasChevron &&
                      expandedItems[item.label] &&
                      trainingSubItems.map((subItem) =>
                        renderNavItem(subItem, true)
                      )}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          ))}

          {authUser && authUser.userType === USER_TYPE.FOLLOWER && (
            <Box
              flexGrow={1}
              display='flex'
              flexDirection='column'
              alignItems='center'
              px={2}
              pt={2}
            >
              <CreatePersonaButton />
            </Box>
          )}
        </ScrollableBox>
      </Box>
    </StyledDrawer>
  );
};

export default ProfileNav;
